import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import PageHeader from '../../../../components/PageHeader/PageHeader'
import moment from 'moment'
import API from '../../../../services/API'
import AnDateField from '../../../../components/DateTime/DateField'
import CustomDataGrid from '../../../../components/DataGrid/CustomDataGrid'
import {
  INCIDENT_STATUSES,
  INCIDENT_TYPES,
  OPEN_STATUSES,
  statusColor,
} from '../incidentConstants'

// MUI
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import AddIcon from '@mui/icons-material/Add'
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined'

const PRESETS = [
  {
    key: 'open',
    label: 'Open',
    caption: 'Awaiting action',
    statuses: OPEN_STATUSES,
    countKey: 'open',
    tone: 'warning',
  },
  {
    key: 'new',
    label: 'New',
    caption: 'Not yet reviewed',
    statuses: ['New'],
    countKey: 'newCount',
    tone: 'info',
  },
  {
    key: 'completed',
    label: 'Completed',
    caption: 'Investigation finished',
    statuses: ['Completed'],
    countKey: 'completed',
    tone: 'success',
  },
  {
    key: 'closed',
    label: 'Closed',
    caption: 'No action required',
    statuses: ['Closed – No Action Required'],
    countKey: 'closed',
    tone: 'default',
  },
  {
    key: 'all',
    label: 'All Incidents',
    caption: 'Everything on record',
    statuses: [],
    countKey: 'total',
    tone: 'primary',
  },
]

const PresetTile = ({ preset, count, active, onClick }) => (
  <Card
    variant="outlined"
    onClick={onClick}
    sx={{
      p: 1.5,
      borderRadius: 2.5,
      cursor: 'pointer',
      minWidth: 0,
      borderColor: active ? 'primary.main' : 'divider',
      borderWidth: active ? 2 : 1,
      bgcolor: active ? 'action.hover' : 'background.paper',
      transition: 'all 0.15s ease',
      '&:hover': { borderColor: 'primary.light' },
    }}
  >
    <Typography
      component="div"
      variant="caption"
      noWrap
      sx={{
        color: 'text.secondary',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        fontSize: '0.66rem',
      }}
    >
      {preset.label}
    </Typography>
    <Stack direction="row" spacing={0.75} alignItems="center">
      {preset.tone !== 'default' && (
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            bgcolor: `${preset.tone}.main`,
            flexShrink: 0,
          }}
        />
      )}
      <Typography sx={{ fontWeight: 800, fontSize: '1.1rem' }} noWrap>
        {count ?? '—'}
      </Typography>
    </Stack>
    <Typography component="div" variant="caption" color="text.secondary" noWrap>
      {preset.caption}
    </Typography>
  </Card>
)

const IncidentList = () => {
  const [incidents, setIncidents] = useState([])
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  })
  const [total, setTotal] = useState(0)
  const [summary, setSummary] = useState(null)
  const [activePreset, setActivePreset] = useState('open')
  const [search, setSearch] = useState('')
  const [searchStartDate, setSearchStartDate] = useState(null)
  const [searchEndDate, setSearchEndDate] = useState(null)
  const [searchType, setSearchType] = useState('')
  const [searchStatuses, setSearchStatuses] = useState(OPEN_STATUSES)
  const [loading, setLoading] = useState(true)
  const [fetchError, setFetchError] = useState(false)
  const debounceRef = useRef(null)

  const navigate = useNavigate()

  const buildQuery = (statuses, pagination) => {
    const params = new URLSearchParams({
      page: pagination.page + 1,
      limit: pagination.pageSize,
      statuses: statuses.join(','),
    })
    if (search.trim()) params.set('search', search.trim())
    if (searchStartDate?.isValid?.())
      params.set('startDate', searchStartDate.toISOString())
    if (searchEndDate?.isValid?.())
      params.set('endDate', searchEndDate.toISOString())
    if (searchType) params.set('type', searchType)
    return params.toString()
  }

  const loadIncidents = (statuses = searchStatuses, pagination = paginationModel) => {
    setLoading(true)
    API.get(`/incidents?${buildQuery(statuses, pagination)}`)
      .then((res) => {
        if (res.status === 200) {
          setIncidents(res.data.data)
          setTotal(res.data.totalRecords?.[0]?.total || 0)
          setFetchError(false)
        } else {
          setFetchError(true)
        }
      })
      .catch(() => setFetchError(true))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    API.get('/incidents-summary').then((res) => {
      if (res.status === 200) setSummary(res.data)
    })
  }, [])

  useEffect(() => {
    loadIncidents()
  }, [paginationModel])

  // Debounced server search on filter change
  const firstRunRef = useRef(true)
  useEffect(() => {
    if (firstRunRef.current) {
      firstRunRef.current = false
      return
    }
    clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      setPaginationModel((prev) =>
        prev.page === 0 ? { ...prev } : { ...prev, page: 0 },
      )
    }, 400)
    return () => clearTimeout(debounceRef.current)
  }, [search, searchStartDate, searchEndDate, searchType, searchStatuses])

  const handlePreset = (preset) => {
    setActivePreset(preset.key)
    setSearchStatuses(preset.statuses)
  }

  return (
    <Stack gap={1.5}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        useFlexGap
        gap={1}
      >
        <Box>
          <PageHeader />
          <Typography variant="caption" color="text.secondary">
            {summary
              ? `${summary.last30Days} incident${
                  summary.last30Days === 1 ? '' : 's'
                } in the last 30 days`
              : ''}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/report-incident')}
          sx={{ borderRadius: 2, textTransform: 'none' }}
        >
          Report an Incident
        </Button>
      </Stack>

      <Box
        display="grid"
        gap={1.5}
        gridTemplateColumns={{ xs: '1fr 1fr', sm: '1fr 1fr 1fr', lg: 'repeat(5, 1fr)' }}
      >
        {PRESETS.map((preset) => (
          <PresetTile
            key={preset.key}
            preset={preset}
            count={summary?.[preset.countKey]}
            active={activePreset === preset.key}
            onClick={() => handlePreset(preset)}
          />
        ))}
      </Box>

      <Paper
        elevation={0}
        sx={{
          p: 1.25,
          borderRadius: 2.5,
          border: (t) => `1px solid ${t.palette.divider}`,
        }}
      >
        <Stack
          direction="row"
          gap={1}
          flexWrap="wrap"
          alignItems="center"
          useFlexGap
        >
          <TextField
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="small"
            placeholder="Search number, description, location…"
            sx={{ flex: 1, minWidth: 220 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <AnDateField
            value={searchStartDate}
            onChange={(newDate) => setSearchStartDate(newDate)}
            label="From"
            size="small"
            sx={{ maxWidth: 150 }}
          />
          <AnDateField
            value={searchEndDate}
            onChange={(newDate) => setSearchEndDate(newDate)}
            label="To"
            size="small"
            sx={{ maxWidth: 150 }}
          />
          <TextField
            select
            value={searchType}
            onChange={(e) => setSearchType(e.target.value)}
            label="Type"
            size="small"
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="">All Types</MenuItem>
            {INCIDENT_TYPES.map((type) => (
              <MenuItem key={type} value={type}>
                {type}
              </MenuItem>
            ))}
          </TextField>
          <Autocomplete
            multiple
            value={searchStatuses}
            onChange={(e, newValue) => {
              setSearchStatuses(newValue)
              setActivePreset(null)
            }}
            options={INCIDENT_STATUSES}
            filterSelectedOptions
            disableCloseOnSelect
            size="small"
            sx={{ flex: 1, minWidth: 240 }}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={option}
                  label={option}
                  size="small"
                  color={statusColor(option)}
                  sx={{ borderRadius: 999, fontWeight: 600 }}
                />
              ))
            }
            renderInput={(params) => (
              <TextField {...params} label="Status" size="small" />
            )}
          />
        </Stack>
      </Paper>

      {loading && <LinearProgress />}
      {fetchError && (
        <Alert severity="error">
          Unable to load incidents. Please try again later.
        </Alert>
      )}
      <CustomDataGrid
        norowsoverlay={
          <Alert
            severity="info"
            icon={<ReportProblemOutlinedIcon />}
            action={
              <Button
                size="small"
                color="inherit"
                onClick={() => navigate('/report-incident')}
              >
                Report an Incident
              </Button>
            }
          >
            No incidents match these filters. Staff can report incidents from
            the sidebar or the + quick-add menu — reports then appear here for
            review and investigation.
          </Alert>
        }
        rows={incidents}
        columns={[
          {
            headerName: 'ID',
            field: 'incidentNumber',
            width: 80,
            renderCell: ({ row }) => (
              <Chip
                size="small"
                color="anPurple"
                label={`#${row.incidentNumber}`}
                sx={{ borderRadius: 1, fontWeight: 700 }}
              />
            ),
          },
          {
            headerName: 'Status',
            field: 'status',
            minWidth: 130,
            renderCell: ({ row }) => (
              <Chip
                size="small"
                label={row.status}
                color={statusColor(row.status)}
                sx={{ borderRadius: 1, fontWeight: 600 }}
              />
            ),
          },
          { headerName: 'Type', field: 'incidentType', minWidth: 150 },
          {
            headerName: 'Description',
            flex: 1,
            minWidth: 240,
            field: 'description',
            renderCell: ({ row }) => (
              <Tooltip title={row.description ?? ''}>
                <Typography variant="body2" noWrap>
                  {row.description}
                </Typography>
              </Tooltip>
            ),
          },
          {
            headerName: 'Date',
            field: 'incidentDate',
            minWidth: 110,
            renderCell: ({ row }) =>
              row.incidentDate
                ? moment(row.incidentDate).format('DD/MM/YYYY')
                : '—',
          },
          {
            headerName: 'Assigned To',
            field: 'responsibleManager',
            minWidth: 160,
            renderCell: ({ row }) =>
              row.responsibleManager?.userAccount ? (
                <Stack direction="row" gap={1} alignItems="center">
                  <Avatar
                    src={row.responsibleManager.userAccount.photograph}
                    sx={{ width: 22, height: 22 }}
                  />
                  <Typography variant="body2" noWrap>
                    {row.responsibleManager.userAccount.firstName}{' '}
                    {row.responsibleManager.userAccount.lastName}
                  </Typography>
                </Stack>
              ) : (
                <Chip
                  label="Unassigned"
                  variant="outlined"
                  sx={{ borderRadius: 1 }}
                  size="small"
                />
              ),
          },
        ]}
        rowCount={total}
        pagination
        paginationMode="server"
        paginationModel={paginationModel}
        onPaginationModelChange={(model) => setPaginationModel(model)}
        pageSizeOptions={[10, 25, 50, 100]}
        disableRowSelectionOnClick
        onRowClick={({ row }) => navigate(row._id)}
        getRowId={(row) => row._id}
        sx={{
          '.MuiDataGrid-cell:focus': { outline: 'none' },
          '& .MuiDataGrid-row:hover': { cursor: 'pointer' },
        }}
        slots={{ toolbar: () => null }}
      />
    </Stack>
  )
}

export default IncidentList
